import React from 'react';
import { motion } from 'framer-motion';
import { Copy, Check, ArrowRight, ArrowDown, Sparkles, Image as ImageIcon, Upload, Database, Globe, Layers, ExternalLink, Pencil, Cloud, Key, QrCode } from 'lucide-react';
import { EditableText, EditableImage, useAdmin } from './AdminContentEditor';
import SEO from './SEO';

const InlineContentManagementPage = () => {
  const { isAdmin, isEditMode, sectionContent } = useAdmin();
  const [copiedPrompt, setCopiedPrompt] = React.useState(null);

  const hasImage = (sectionId, field) => {
    const val = sectionContent?.[sectionId]?.[field] || sectionContent?.[sectionId]?.custom_fields?.[field];
    return val && val.length > 0;
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedPrompt(id);
    setTimeout(() => setCopiedPrompt(null), 2000);
  };

  const PROMPT_BACKEND = `Build a content management system for my website.

BACKEND (server.py):
- Add these endpoints:
  GET  /api/admin/sections/{section_id}  — public, returns saved content for that section (no auth)
  POST /api/admin/sections               — admin only, saves content (requires auth)
  POST /api/admin/upload                 — admin only, uploads image file to Cloudinary and returns the URL
  GET  /api/admin/media-library          — public, lists all previously uploaded Cloudinary images

- Admin authentication (pick one):

  OPTION A — Google Auth (recommended for production):
  Use Emergent's Google OAuth. Only allow these admin emails: [YOUR_EMAIL@gmail.com]
  Store approved admin emails in MongoDB collection "admin_emails".
  
  OPTION B — Magic Link (quick dev/testing shortcut):
  POST /api/auth/magic — accepts "key" param. If key matches env var ADMIN_MAGIC_KEY, create a session token and return it. Use a long random key.

- MongoDB collection "admin_sections" stores content as: { section_id, field_name: value, updated_at }
- Cloudinary config reads from .env: CLOUDINARY_CLOUD_NAME, CLOUDINARY_API_KEY, CLOUDINARY_API_SECRET

- CORS: allow all origins
- Every GET is public (no auth). Every POST checks for a valid admin session.`;

  const PROMPT_FRONTEND = `Now build the frontend inline editor components.

Create an AdminContentEditor.jsx with these exports:

1. AdminProvider (context wrapper)
   - On mount: checks if user is authenticated (Google session or localStorage token)
   - If authenticated and email is approved: sets isAdmin = true
   - Loads ALL section content from GET /api/admin/sections on mount
   - Provides: { isAdmin, isEditMode, toggleEditMode, sectionContent, updateSectionContent }
   - Wrap your entire App with <AdminProvider>

2. EditableText component
   Props: sectionId, field, defaultValue, as (h1/p/span), className
   - Non-admin: renders the saved text (or defaultValue) as a normal element
   - Admin in edit mode: element becomes contentEditable, blue dashed border appears
   - On blur: auto-saves to POST /api/admin/sections

3. EditableImage component
   Props: sectionId, field, defaultSrc, alt, className
   - Non-admin: renders normal <img>
   - Admin in edit mode: purple overlay on hover with camera icon
   - Click opens a modal with two tabs: "Upload New" and "Media Library"
   - Upload New: file picker → compress image client-side (max 1920px, 80% JPEG quality using Canvas) → POST /api/admin/upload → saves returned URL
   - Media Library: fetches GET /api/admin/media-library → grid of previously uploaded images → click to select
   - Client-side compression is critical — deployment proxies reject files over ~1MB

4. EditModeToggle component
   - Floating button (bottom-right) only visible to admins
   - Toggles between view mode and edit mode
   - Shows "Editing" state with green indicator

5. Login page
   - Google Auth: button that triggers Emergent Google OAuth flow
   - Magic Link (dev only): reads "key" from URL params, sends to backend, stores token on success
   - After login: redirect to homepage, admin can toggle edit mode`;

  const PROMPT_CONNECT = `Now connect everything and add inline editing to my homepage.

1. In App.js:
   - Wrap everything with <AdminProvider>
   - Add <EditModeToggle /> inside the provider (renders globally)
   - Add login route (Google Auth callback or /auth/magic for magic link)

2. On the homepage (or any page), replace static text and images:

   BEFORE: <h1>Welcome to My Site</h1>
   AFTER:  <h1><EditableText sectionId="homepage" field="title" defaultValue="Welcome to My Site" as="span" /></h1>

   BEFORE: <img src="/hero.jpg" alt="Hero" />
   AFTER:  <EditableImage sectionId="homepage" field="hero" defaultSrc="/hero.jpg" alt="Hero" className="w-full h-full object-cover" />

3. That's it. Every EditableText and EditableImage:
   - Shows saved content to everyone (loads from DB on page load)
   - Becomes editable when an admin toggles edit mode
   - Auto-saves changes to MongoDB
   - Images persist on Cloudinary (survive deployments)

4. To log in as admin:
   - Google Auth: Sign in with your approved Google account
   - Magic Link (dev only): Visit https://your-site.com/auth/magic?key=YOUR_KEY
   Toggle edit mode with the floating button — click any text or image to edit.`;

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Inline Content Management System" description="Build Once, Edit Forever — Workshop on building inline content management for Emergent websites" url="/InlineContentManagement" />

      {/* ===== HERO ===== */}
      <section className="px-6 md:px-12 lg:px-24 pt-20 pb-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Live Workshop
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-4">
            <EditableText sectionId="icm-hero" field="title" defaultValue="Build Once, Edit Forever" as="span" />
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="text-xl md:text-2xl text-gray-500 font-medium">
            <EditableText sectionId="icm-hero" field="subtitle" defaultValue="Inline Content Management for Emergent Websites" as="span" />
          </motion.p>
        </div>
      </section>

      {/* ===== MEET YOUR GUIDE ===== */}
      <section className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8 md:p-10">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
            </div>
            <div className="relative">
              <p className="text-emerald-400 text-sm font-medium uppercase tracking-wide mb-6">Meet Your Guide</p>
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 rounded-2xl overflow-hidden ring-2 ring-white/20">
                    <EditableImage sectionId="icm-guide" field="image" defaultSrc="https://res.cloudinary.com/dwqcaivlq/image/upload/v1737668582/admin_uploads/k4o8rkvy0jjl64xofjaa.jpg" alt="Naomi Thomas" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    <EditableText sectionId="icm-guide" field="name" defaultValue="Naomi Thomas" as="span" />
                  </h3>
                  <p className="text-gray-400 mb-6">
                    <EditableText sectionId="icm-guide" field="bio" defaultValue="Building on Emergent since January 2026. Focused on turning one time builds into systems that teams can update and scale over time." as="span" />
                  </p>
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-3">Sites Built on Emergent</p>
                  <div className="grid grid-cols-2 gap-4">
                    {[{ field: 'site1', name: 'Data Dreaming', url: 'datadreaming.cloud' }, { field: 'site2', name: 'techbaenae', url: 'techbaenae.com' }].map((site) => (
                      <div key={site.field} className="overflow-hidden rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all">
                        <div className="aspect-video bg-gray-800 relative">
                          <div className="absolute inset-0">
                            <EditableImage sectionId="icm-guide" field={`${site.field}-image`} defaultSrc="" alt={site.name} className="w-full h-full object-cover" />
                          </div>
                          {!hasImage('icm-guide', `${site.field}-image`) && !isEditMode && (
                            <div className="absolute inset-0 flex items-center justify-center text-gray-500 pointer-events-none">
                              <div className="text-center"><ImageIcon className="w-6 h-6 mx-auto mb-1" /><span className="text-xs">Site Preview</span></div>
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="text-white text-sm font-medium"><EditableText sectionId="icm-guide" field={`${site.field}-name`} defaultValue={site.name} as="span" /></p>
                          <p className="text-gray-500 text-xs"><EditableText sectionId="icm-guide" field={`${site.field}-url`} defaultValue={site.url} as="span" /></p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== WHAT WE'RE BUILDING + WHY ===== */}
      <section className="px-6 md:px-12 lg:px-24 py-16 bg-gradient-to-b from-white to-purple-50/30">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              <EditableText sectionId="icm-demo" field="section-title" defaultValue="What We're Building" as="span" />
            </h2>
            <p className="text-gray-600 mb-8">
              <EditableText sectionId="icm-demo" field="section-subtitle" defaultValue="A content management system where you edit your live site directly — no admin panel, no CMS dashboard. Click, type, save." as="span" />
            </p>
          </motion.div>

          {/* Architecture Flow */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
            <div className="flex flex-col md:flex-row flex-wrap items-center justify-center gap-3 md:gap-4 p-6 bg-gray-900 rounded-2xl">
              {['Admin clicks text', 'Types new content', 'Auto-saves to DB', 'Everyone sees it'].map((step, i) => (
                <React.Fragment key={i}>
                  {i > 0 && (
                    <>
                      <ArrowDown className="w-5 h-5 text-gray-600 md:hidden" />
                      <ArrowRight className="w-5 h-5 text-gray-600 hidden md:block" />
                    </>
                  )}
                  <div className={`px-4 py-2 rounded-xl text-sm font-medium border ${
                    ['bg-purple-500/20 border-purple-500/30 text-purple-400', 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400', 'bg-blue-500/20 border-blue-500/30 text-blue-400', 'bg-orange-500/20 border-orange-500/30 text-orange-400'][i]
                  }`}>{step}</div>
                </React.Fragment>
              ))}
            </div>
          </motion.div>

          {/* Visual demos side by side */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="grid md:grid-cols-2 gap-6 mb-10">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b border-gray-200">
                <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400" /><div className="w-3 h-3 rounded-full bg-yellow-400" /><div className="w-3 h-3 rounded-full bg-green-400" /></div>
                <span className="text-xs text-gray-500 ml-2">Inline Text Editing</span>
              </div>
              <div className="p-6">
                <div className="mb-3">
                  <div className="border-2 border-dashed border-purple-300 bg-purple-50 rounded-lg p-3 cursor-text">
                    <h3 className="text-lg font-bold text-gray-900"><EditableText sectionId="icm-demo" field="mock-headline" defaultValue="Click to edit this headline" as="span" /></h3>
                  </div>
                  <span className="text-xs text-purple-500 mt-1 flex items-center gap-1"><Pencil className="w-3 h-3" /> click any text to edit</span>
                </div>
                <div className="border-2 border-dashed border-blue-300 bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600"><EditableText sectionId="icm-demo" field="mock-tagline" defaultValue="This tagline saves automatically when you click away" as="span" /></p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b border-gray-200">
                <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400" /><div className="w-3 h-3 rounded-full bg-yellow-400" /><div className="w-3 h-3 rounded-full bg-green-400" /></div>
                <span className="text-xs text-gray-500 ml-2">Image Upload</span>
              </div>
              <div className="p-6">
                <div className="border-2 border-dashed border-emerald-300 bg-emerald-50 rounded-lg p-3 flex items-center gap-4">
                  <div className="w-20 h-20 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Upload className="w-8 h-8 text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Upload from computer</p>
                    <p className="text-xs text-gray-500">or pick from Media Library</p>
                    <p className="text-xs text-emerald-600 mt-1">Auto-compressed + stored on Cloudinary</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </section>


      {/* ===== BODY FRUIT — MOCK BRAND EXAMPLE ===== */}
      <section className="px-6 md:px-12 lg:px-24 py-16 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-6">
            <p className="text-orange-600 font-mono text-sm mb-2">EXAMPLE</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              <EditableText sectionId="icm-brand" field="section-title" defaultValue="The Brand We'll Use" as="span" />
            </h2>
            <p className="text-gray-600"><EditableText sectionId="icm-brand" field="section-subtitle" defaultValue="Meet the mock brand we'll build this system around during the demo." as="span" /></p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-gradient-to-br from-orange-100 via-rose-50 to-amber-50 rounded-3xl p-8 border border-orange-200 shadow-lg">
            <div className="flex flex-col md:flex-row items-center gap-6 mb-6">
              <div className="w-40 h-40 rounded-2xl bg-white border-2 border-dashed border-orange-300 overflow-hidden relative">
                <div className="absolute inset-0"><EditableImage sectionId="icm-brand" field="logo" defaultSrc="" alt="Brand Logo" className="w-full h-full object-cover" /></div>
                {!hasImage('icm-brand', 'logo') && !isEditMode && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-orange-400 pointer-events-none"><Upload className="w-8 h-8 mb-2" /><span className="text-xs font-medium">Brand Logo</span></div>
                )}
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-3xl font-bold text-gray-900 mb-2"><EditableText sectionId="icm-brand" field="name" defaultValue="Body Fruit" as="span" /></h3>
                <p className="text-gray-600 mb-3"><EditableText sectionId="icm-brand" field="description" defaultValue="Natural wellness products made with real fruit extracts." as="span" /></p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  {['tag1', 'tag2', 'tag3'].map((tag, i) => (
                    <span key={tag} className={`px-3 py-1 rounded-full text-sm font-medium ${['bg-orange-100 text-orange-700', 'bg-rose-100 text-rose-700', 'bg-amber-100 text-amber-700'][i]}`}>
                      <EditableText sectionId="icm-brand" field={tag} defaultValue={['Products', 'Wellness', 'Natural'][i]} as="span" />
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[
                { field: 'hero-image', label: 'Hero Image', color: 'orange' },
                { field: 'product-image', label: 'Product Grid', color: 'rose' },
                { field: 'about-image', label: 'About Section', color: 'amber' }
              ].map((area) => (
                <div key={area.field} className="bg-white/80 backdrop-blur rounded-xl p-4 border border-orange-100">
                  <div className={`w-full h-20 bg-${area.color}-100 rounded-lg mb-2 relative overflow-hidden`}>
                    <div className="absolute inset-0"><EditableImage sectionId="icm-brand" field={area.field} defaultSrc="" alt={area.label} className="w-full h-full object-cover" /></div>
                    {!hasImage('icm-brand', area.field) && !isEditMode && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><ImageIcon className={`w-6 h-6 text-${area.color}-400`} /></div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 text-center"><EditableText sectionId="icm-brand" field={`${area.field}-label`} defaultValue={area.label} as="span" /></p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== WHITEBOARD ===== */}
      <section className="px-6 md:px-12 lg:px-24 py-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
              <EditableText sectionId="icm-whiteboard" field="title" defaultValue="System Diagram" as="span" />
            </h2>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-3xl overflow-hidden border-2 border-gray-200 shadow-xl bg-white">
            <div className="aspect-[16/9] relative">
              <div className="absolute inset-0"><EditableImage sectionId="icm-whiteboard" field="image" defaultSrc="" alt="System Diagram" className="w-full h-full object-contain" /></div>
              {!hasImage('icm-whiteboard', 'image') && !isEditMode && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 pointer-events-none">
                  <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" /></svg>
                  <span className="text-lg font-medium">Whiteboard / System Diagram</span><span className="text-sm mt-1">Upload your whiteboard image here</span>
                </div>
              )}
            </div>
          </motion.div>
          <p className="text-center text-gray-500 mt-4 text-sm"><EditableText sectionId="icm-whiteboard" field="caption" defaultValue="Draw your system architecture here during the workshop" as="span" /></p>
        </div>
      </section>

      {/* ===== SETUP: CLOUDINARY ===== */}
      <section className="px-6 md:px-12 lg:px-24 py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
            <p className="text-emerald-400 font-mono text-sm mb-2">BEFORE WE BUILD</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              <EditableText sectionId="icm-setup" field="title" defaultValue="One Setup Step" as="span" />
            </h2>
            <p className="text-gray-400"><EditableText sectionId="icm-setup" field="subtitle" defaultValue="Create a free Cloudinary account so images persist after every deployment." as="span" /></p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white/5 rounded-2xl p-8 border border-white/10 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Cloud className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1">Cloudinary (Free Tier)</h3>
                <p className="text-gray-400 text-sm">25GB storage. Images survive every deployment. Takes 2 minutes.</p>
              </div>
            </div>
            <div className="space-y-4">
              {[
                { step: '1', text: 'Go to cloudinary.com and sign up (free)' },
                { step: '2', text: 'From your dashboard, copy these 3 values: Cloud Name, API Key, API Secret' },
                { step: '3', text: 'Add them to your Emergent project\'s backend environment variables (Secrets)' }
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-400 text-sm font-bold">{item.step}</span>
                  </div>
                  <p className="text-gray-300 text-sm pt-0.5">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 rounded-xl bg-black/30 font-mono text-sm text-gray-400">
              <p className="text-emerald-400 mb-1"># Backend .env</p>
              <p>CLOUDINARY_CLOUD_NAME=your_cloud_name</p>
              <p>CLOUDINARY_API_KEY=your_api_key</p>
              <p>CLOUDINARY_API_SECRET=your_api_secret</p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-4 rounded-xl bg-white/5 border border-amber-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Key className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium text-amber-300">Admin Auth</span>
            </div>
            <p className="text-sm text-gray-400">
              <EditableText sectionId="icm-setup" field="note" defaultValue="MongoDB is built into Emergent. For admin authentication, the prompts give you two options: Google Auth (recommended for production — secure OAuth, no keys to leak) or magic link (quick dev shortcut — a secret URL grants access). The prompts below handle whichever you choose." as="span" />
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== BUILD: THE PROMPTS ===== */}
      <section className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
            <p className="text-purple-600 font-mono text-sm mb-2">LIVE BUILD</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              <EditableText sectionId="icm-build" field="title" defaultValue="3 Prompts. That's It." as="span" />
            </h2>
            <p className="text-gray-600">
              <EditableText sectionId="icm-build" field="subtitle" defaultValue="Paste each prompt into Emergent in order. Split-screen so we can watch it build in real time." as="span" />
            </p>
          </motion.div>

          {/* Prompt 1 */}
          <PromptCard
            number="1"
            title="Backend + Auth + Cloudinary"
            description="Builds the API, image uploads, and admin authentication (Google Auth or magic link)."
            prompt={PROMPT_BACKEND}
            color="blue"
            icon={Database}
            copiedPrompt={copiedPrompt}
            onCopy={copyToClipboard}
          />

          {/* Prompt 2 */}
          <PromptCard
            number="2"
            title="Frontend Inline Editor"
            description="Creates AdminProvider, EditableText, EditableImage, edit toggle, and the login page."
            prompt={PROMPT_FRONTEND}
            color="purple"
            icon={Layers}
            copiedPrompt={copiedPrompt}
            onCopy={copyToClipboard}
          />

          {/* Prompt 3 */}
          <PromptCard
            number="3"
            title="Connect + Start Editing"
            description="Wires everything together and shows how to make any text or image on your site editable."
            prompt={PROMPT_CONNECT}
            color="emerald"
            icon={Globe}
            copiedPrompt={copiedPrompt}
            onCopy={copyToClipboard}
          />
        </div>
      </section>

      {/* ===== RESULT ===== */}
      <section className="px-6 md:px-12 lg:px-24 py-16 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8 md:p-10" data-testid="icm-end-product">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500 rounded-full blur-3xl" />
            </div>
            <div className="relative flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1 w-full">
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <div className="aspect-video bg-gray-800 relative">
                    <div className="absolute inset-0"><EditableImage sectionId="icm-demo" field="site-preview" defaultSrc="" alt="Finished site preview" className="w-full h-full object-cover" /></div>
                    {!hasImage('icm-demo', 'site-preview') && !isEditMode && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 pointer-events-none"><ImageIcon className="w-10 h-10 mb-2" /><span className="text-sm">Finished Site Screenshot</span></div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 text-center md:text-left md:max-w-xs">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3"><EditableText sectionId="icm-demo" field="cta-title" defaultValue="See the Finished Product" as="span" /></h3>
                <p className="text-gray-400 mb-6"><EditableText sectionId="icm-demo" field="cta-description" defaultValue="A fully working site built live during the workshop with inline content management." as="span" /></p>
                <a href={sectionContent?.['icm-demo']?.['cta-url'] || '#'} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (isEditMode) e.preventDefault(); }} className={`inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-emerald-500/25 ${isEditMode ? 'cursor-default' : ''}`} data-testid="icm-visit-site-btn">
                  <EditableText sectionId="icm-demo" field="cta-button" defaultValue="Visit Live Site" as="span" />
                  <ExternalLink className="w-4 h-4" />
                </a>
                {isAdmin && isEditMode && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">Button URL:</p>
                    <EditableText sectionId="icm-demo" field="cta-url" defaultValue="https://your-site.emergentagent.com" className="text-xs text-gray-400 italic" as="span" />
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== BRAIN FOOD / CLOSING ===== */}
      <section className="px-6 md:px-12 lg:px-24 py-16 bg-gradient-to-b from-white to-emerald-50">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-3xl p-8 md:p-12 border border-emerald-200 shadow-lg shadow-emerald-100">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-40 h-40 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-emerald-300">
                <div className="text-center"><QrCode className="w-12 h-12 text-emerald-500 mx-auto mb-2" /><span className="text-xs text-emerald-600 font-medium">QR Code</span></div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3"><EditableText sectionId="icm-brainfood" field="title" defaultValue="Brain Food" as="span" /></h2>
                <p className="text-gray-600 mb-4 text-lg"><EditableText sectionId="icm-brainfood" field="description" defaultValue="All prompts, setup guides, and troubleshooting — available to download." as="span" /></p>
                <div className="flex flex-wrap gap-2 mb-4 justify-center md:justify-start">
                  {['all 3 prompts', 'Cloudinary setup', 'admin auth', 'troubleshooting'].map((item) => (
                    <span key={item} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">{item}</span>
                  ))}
                </div>
                <p className="text-emerald-600 font-medium"><EditableText sectionId="icm-brainfood" field="access" defaultValue="Access via QR code or RSVP confirmation" as="span" /></p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="h-12" />
    </div>
  );
};

/* ── Prompt Card Component ── */
const PromptCard = ({ number, title, description, prompt, color, icon: Icon, copiedPrompt, onCopy }) => {
  const id = `prompt-${number}`;
  const colorMap = {
    blue: { bg: 'from-blue-500 to-indigo-500', badge: 'bg-blue-100 text-blue-700', accent: 'bg-blue-500' },
    purple: { bg: 'from-purple-500 to-pink-500', badge: 'bg-purple-100 text-purple-700', accent: 'bg-purple-500' },
    emerald: { bg: 'from-emerald-500 to-teal-500', badge: 'bg-emerald-100 text-emerald-700', accent: 'bg-emerald-500' }
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10" data-testid={`icm-${id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${c.bg} flex items-center justify-center`}>
          <span className="text-white font-bold text-sm">{number}</span>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <p className="text-gray-500 text-sm">{description}</p>
        </div>
      </div>
      <div className="relative bg-gray-900 rounded-2xl p-5 overflow-hidden">
        <div className={`absolute top-0 left-0 w-1 h-full ${c.accent}`} />
        <button onClick={() => onCopy(prompt, id)} className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10 z-10" title="Copy prompt" data-testid={`copy-${id}`}>
          {copiedPrompt === id ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
        </button>
        <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono pr-10 leading-relaxed">{prompt}</pre>
      </div>
    </motion.div>
  );
};

export default InlineContentManagementPage;
